import ProductLink from "@/components/productQuickView/ProductLink";
import type { Row } from "../byMonthTypes";
import { rowChanged, type ByMonthDiff } from "./byMonthDiff";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const qty = new Intl.NumberFormat("en-US");

function monthHead(iso: string): string {
  const [y, m] = iso.split("-");
  return `${MONTHS[parseInt(m, 10) - 1]} ${y.slice(2)}`;
}

// The published value under a changed cell, GitHub-style: red strikethrough
// "was …", or "new" in green when there was no row before.
function WasLine({ was, format }: { was: number | null | undefined; format: (n: number) => string }) {
  if (was == null) return <div className="mt-0.5 text-[10px] font-medium" style={{ color: "var(--success)" }}>new</div>;
  return (
    <div className="mt-0.5 text-[10px] tabular-nums line-through" style={{ color: "var(--destructive)" }}>
      was {format(was)}
    </div>
  );
}

// Read-only products × months table for the submit and review pages: the
// draft's quantity per cell with the published value under every changed
// one. The editing grid stays out of these pages on purpose.
export default function ByMonthDiffTable({ rows, months, diff, changesOnly, emptyMessage }: {
  rows: Row[];
  months: string[];
  diff: ByMonthDiff | null;
  changesOnly: boolean;
  emptyMessage: string;
}) {
  const visible = changesOnly && diff ? rows.filter((r) => rowChanged(r, diff)) : rows;

  if (visible.length === 0) {
    return <p className="px-4 py-6 text-center text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="border-b text-left text-muted-foreground" style={{ borderColor: "var(--border)" }}>
            <th className="sticky left-0 bg-card px-3 py-2 font-medium">Product</th>
            {months.map((m) => (
              <th key={m} className="px-2 py-2 text-right font-medium tabular-nums">{monthHead(m)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => {
            const qWas = diff?.quantity_was[row.product_external_id] ?? {};
            return (
              <tr key={row.product_external_id} className="border-b" style={{ borderColor: "var(--border)" }}>
                <td className="sticky left-0 bg-card px-3 py-1.5">
                  <ProductLink externalId={row.product_external_id} className="text-[12px] font-medium">
                    {row.product_name}
                  </ProductLink>
                  {row.product_sku && <div className="text-[10px] text-muted-foreground">{row.product_sku}</div>}
                </td>
                {row.months.map((cell) => {
                  const qChanged = cell.period_date in qWas;
                  return (
                    <td key={cell.period_date} className="px-2 py-1.5 text-right align-top tabular-nums">
                      <div className={qChanged ? "font-medium" : "text-foreground"} style={qChanged ? { color: "var(--success)" } : undefined}>
                        {cell.filled ? qty.format(cell.quantity) : "—"}
                      </div>
                      {qChanged && <WasLine was={qWas[cell.period_date]} format={(n) => qty.format(n)} />}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
